export const PRIVACY_UPDATED = "10 August 2026";

export const PRIVACY_SECTIONS = [
  {
    n: 1,
    heading: "About this Privacy Policy",
    blocks: [
      { type: "p", text: "Behaviour Operating System is the business name of Tristan Todd trading as Behaviour Operating System, ABN 79 382 388 533 (“BOS”, “we”, “us” or “our”)." },
      { type: "p", text: "This Privacy Policy explains how we collect, hold, use, disclose and protect personal information when you:" },
      {
        type: "list",
        items: [
          "visit the Behaviour Operating System website;",
          "complete an online diagnostic, assessment, questionnaire or form;",
          "subscribe to our newsletter or other communications;",
          "enquire about our services;",
          "participate in a consulting engagement, workshop or research activity;",
          "download material from our website; or",
          "otherwise communicate or interact with us.",
        ],
      },
      { type: "p", text: "We are based in Sydney, New South Wales, Australia." },
      { type: "p", text: "We intend to handle personal information consistently with the Privacy Act 1988 (Cth) and the Australian Privacy Principles, whether or not every provision of that legislation applies to us in a particular circumstance." },
    ],
  },
  {
    n: 2,
    heading: "What is personal information?",
    blocks: [
      { type: "p", text: "Personal information is information or an opinion about an identified individual, or an individual who is reasonably identifiable." },
      { type: "p", text: "Depending on how you interact with us, the personal information we collect may include:" },
      {
        type: "list",
        items: [
          "your name;",
          "job title, role or profession;",
          "employer or organisation;",
          "business email address;",
          "telephone number;",
          "location;",
          "information included in an enquiry or consultation request;",
          "newsletter subscription preferences;",
          "website activity and technical information;",
          "diagnostic, questionnaire or assessment responses;",
          "feedback, testimonials or survey responses;",
          "information provided during a workshop or consulting engagement; and",
          "billing, payment and transaction information.",
        ],
      },
      { type: "p", text: "We do not generally seek to collect sensitive information through the website." },
      { type: "p", text: "Please do not submit health information, political opinions, union membership information, racial or ethnic information, religious beliefs, criminal history or other sensitive information through general website forms unless we have specifically requested it and explained why it is required." },
    ],
  },
  {
    n: 3,
    heading: "Organisational and diagnostic information",
    blocks: [
      { type: "p", text: "BOS may collect information about organisational practices, operating conditions, leadership behaviours, decision-making, accountability, workflow, role clarity and operational friction." },
      { type: "p", text: "Some of this information may not identify an individual. However, it may become personal information where:" },
      {
        type: "list",
        items: [
          "a person is named;",
          "a role can be linked to a particular individual;",
          "responses contain identifying details; or",
          "the information can reasonably be combined with other information to identify someone.",
        ],
      },
      { type: "p", text: "Where possible, BOS will use aggregated or de-identified information when analysing organisational patterns or preparing reports." },
      { type: "p", text: "Unless expressly agreed otherwise in writing, website diagnostics are designed to identify possible patterns and areas for further investigation. They are not psychometric tests, employee performance evaluations, clinical assessments or formal findings about an individual." },
    ],
  },
  {
    n: 4,
    heading: "How we collect personal information",
    blocks: [
      { type: "p", text: "We may collect personal information:" },
      { type: "sub", text: "Directly from you" },
      { type: "p", text: "For example, when you:" },
      {
        type: "list",
        items: [
          "complete a website form;",
          "complete a diagnostic or questionnaire;",
          "subscribe to communications;",
          "book a consultation;",
          "send us an email;",
          "attend a meeting or workshop;",
          "enter into a consulting engagement; or",
          "provide feedback.",
        ],
      },
      { type: "sub", text: "From your organisation" },
      { type: "p", text: "Where your employer or organisation engages BOS, it may provide us with information required to manage or deliver the engagement." },
      { type: "sub", text: "From publicly available sources" },
      { type: "p", text: "We may collect professional information from public company websites, professional directories, social media platforms or other public sources where reasonably necessary for a legitimate business purpose." },
      { type: "sub", text: "Automatically through the website" },
      { type: "p", text: "Our website and service providers may automatically collect information such as:" },
      {
        type: "list",
        items: [
          "your IP address;",
          "browser type;",
          "device type;",
          "operating system;",
          "pages viewed;",
          "links selected;",
          "referring website;",
          "approximate location;",
          "session duration; and",
          "date and time of access.",
        ],
      },
      { type: "p", text: "This information may be collected through cookies, server logs and similar essential technologies." },
    ],
  },
  {
    n: 5,
    heading: "Why we collect and use personal information",
    blocks: [
      { type: "p", text: "We may collect, hold and use personal information to:" },
      {
        type: "list",
        items: [
          "respond to enquiries;",
          "provide information about BOS;",
          "arrange discovery calls or meetings;",
          "deliver consulting, diagnostic or workshop services;",
          "analyse diagnostic responses;",
          "prepare reports and recommendations;",
          "communicate with clients and prospective clients;",
          "administer contracts, invoices and payments;",
          "operate, maintain and improve our website;",
          "improve our methods, materials and services;",
          "protect our systems and prevent misuse;",
          "comply with legal, regulatory and insurance obligations;",
          "manage complaints or disputes;",
          "maintain business records; and",
          "send newsletters, insights or marketing communications where permitted.",
        ],
      },
      { type: "p", text: "We will not use personal information for an unrelated purpose unless:" },
      {
        type: "list",
        items: [
          "you have consented;",
          "you would reasonably expect the use;",
          "the use is required or authorised by law; or",
          "another lawful basis applies.",
        ],
      },
    ],
  },
  {
    n: 6,
    heading: "Website diagnostic results",
    blocks: [
      { type: "p", text: "The Operational Clarity Review on this website operates as a leader-completed directional review. When you complete it:" },
      {
        type: "list",
        items: [
          "your result is calculated in your browser;",
          "you are not required to provide a name, email address or other personal details to answer the questions or access your result;",
          "your raw answers are not stored in a BOS database;",
          "your raw answers are not sent to BOS;",
          "no persistent result record is generated;",
          "the result does not establish what individual employees understand or experience; and",
          "the result does not establish a definitive root cause.",
        ],
      },
      { type: "p", text: "You may optionally ask us to email a copy of the complete result already shown in your browser. An email address is not required to access that result." },
      {
        type: "list",
        items: [
          "when you request an emailed copy, your email address and a derived summary of the result are transmitted to our website hosting and email-delivery providers solely to deliver the requested email;",
          "the 18 raw answers, individual question-and-response pairs, individual NA/NV values or patterns, numerical result indices, browser session data and optional written example are not transmitted;",
          "requesting an emailed result does not subscribe you to marketing; and",
          "subscription to The Operational Brief occurs only if you select the separate newsletter checkbox and complete the existing confirmation process.",
        ],
      },
      { type: "p", text: "Because the area reviewed and recurring concern are included in the emailed result, do not enter personal, sensitive or confidential information in those fields. Transactional email content and delivery metadata may be processed or retained temporarily by our hosting and email-delivery providers in accordance with their service settings and legal obligations." },
      { type: "p", text: "The result is general, directional information based solely on the answers entered during your browser session. It is not a complete organisational diagnosis." },
      { type: "p", text: "Submitting a diagnostic does not create a consultant-client relationship." },
    ],
  },
  {
    n: 7,
    heading: "Direct marketing and newsletters",
    blocks: [
      { type: "p", text: "We may send you newsletters, articles, invitations, service information or other commercial communications where:" },
      {
        type: "list",
        items: [
          "you have provided express consent;",
          "consent can lawfully be inferred from the circumstances; or",
          "another lawful basis permits the communication.",
        ],
      },
      { type: "p", text: "Our marketing communications will identify the sender and provide a functional way to unsubscribe." },
      { type: "p", text: "You may withdraw your consent at any time by:" },
      {
        type: "list",
        items: [
          "selecting the unsubscribe link in an email;",
          "contacting us at tristan@behaviouroperatingsystem.com; or",
          "using another opt-out method described in the communication.",
        ],
      },
      { type: "p", text: "We will process unsubscribe requests within the period required by Australian law." },
      { type: "p", text: "Unsubscribing from marketing communications will not prevent us from sending necessary service, transaction, contractual or administrative communications." },
    ],
  },
  {
    n: 8,
    heading: "Cookies, analytics and tracking technologies",
    blocks: [
      { type: "p", text: "This website uses only essential technologies required for website operation, security and requested functionality — for example, retaining your diagnostic answers within your own browser session while you complete the Operational Clarity Review." },
      { type: "p", text: "We do not use optional analytics, advertising, session-recording or behavioural-tracking cookies on this website." },
      { type: "p", text: "Where technical information collected by our hosting or email providers constitutes personal information, we handle it in accordance with this Privacy Policy." },
    ],
  },
  {
    n: 9,
    heading: "Disclosure of personal information",
    blocks: [
      { type: "p", text: "We may disclose personal information to service providers that help us operate BOS, including providers of:" },
      {
        type: "list",
        items: [
          "website hosting;",
          "website development and maintenance;",
          "email delivery;",
          "online forms;",
          "cloud storage; and",
          "information technology.",
        ],
      },
      { type: "p", text: "We may also disclose information:" },
      {
        type: "list",
        items: [
          "to contractors assisting with an engagement;",
          "to your organisation where appropriate and authorised;",
          "where required or authorised by law;",
          "to regulators, courts or law-enforcement bodies;",
          "in connection with a proposed sale or restructuring of the business; or",
          "with your consent.",
        ],
      },
      { type: "p", text: "Service providers are only given information reasonably required to perform their functions." },
    ],
  },
  {
    n: 10,
    heading: "Overseas disclosure and storage",
    blocks: [
      { type: "p", text: "Some service providers may store or process information outside Australia." },
      { type: "p", text: "Because cloud-service locations and subcontractors can change, you should contact us if you require current information about the location in which your information may be processed." },
      { type: "p", text: "Where reasonably practicable, we take steps to select reputable providers and require appropriate privacy and security protections." },
    ],
  },
  {
    n: 11,
    heading: "Artificial intelligence tools",
    blocks: [
      { type: "p", text: "BOS may use artificial intelligence tools to assist with activities such as:" },
      {
        type: "list",
        items: [
          "drafting;",
          "document formatting;",
          "summarising non-sensitive information;",
          "research;",
          "analysis;",
          "categorisation;",
          "administrative support; and",
          "improving internal efficiency.",
        ],
      },
      { type: "p", text: "We do not intentionally enter confidential client information, identifiable diagnostic responses or sensitive personal information into public artificial intelligence systems unless:" },
      {
        type: "list",
        items: [
          "this is reasonably necessary;",
          "appropriate safeguards are in place; and",
          "the proposed use is consistent with our agreement with the relevant client or participant.",
        ],
      },
      { type: "p", text: "Material decisions, findings and recommendations remain subject to human review and professional judgement." },
    ],
  },
  {
    n: 12,
    heading: "Data security",
    blocks: [
      { type: "p", text: "We take reasonable steps to protect personal information against:" },
      {
        type: "list",
        items: [
          "misuse;",
          "interference;",
          "loss;",
          "unauthorised access;",
          "unauthorised modification; and",
          "unauthorised disclosure.",
        ],
      },
      { type: "p", text: "These steps may include:" },
      {
        type: "list",
        items: [
          "access controls;",
          "password protection;",
          "multi-factor authentication;",
          "reputable cloud providers;",
          "restricted access to client files;",
          "software updates;",
          "secure deletion;",
          "backup processes; and",
          "staff or contractor confidentiality obligations.",
        ],
      },
      { type: "p", text: "No internet transmission or information-storage system is completely secure. We cannot guarantee absolute security." },
    ],
  },
  {
    n: 13,
    heading: "Data retention",
    blocks: [
      { type: "p", text: "We retain personal information only for as long as reasonably necessary for:" },
      {
        type: "list",
        items: [
          "the purpose for which it was collected;",
          "client-service delivery;",
          "business administration;",
          "legal and taxation requirements;",
          "insurance obligations;",
          "dispute management;",
          "evidence and quality assurance; or",
          "another legitimate business purpose.",
        ],
      },
      { type: "p", text: "When information is no longer required, we will take reasonable steps to delete it or de-identify it, subject to legal, contractual and recordkeeping requirements." },
    ],
  },
  {
    n: 14,
    heading: "Accessing or correcting your information",
    blocks: [
      { type: "p", text: "You may request access to personal information we hold about you or ask us to correct information that is inaccurate, incomplete, out of date, irrelevant or misleading." },
      { type: "p", text: "Requests should be sent to:" },
      { type: "p", text: "Privacy Contact\nBehaviour Operating System\nEmail: tristan@behaviouroperatingsystem.com" },
      { type: "p", text: "We may need to verify your identity before responding." },
      { type: "p", text: "We will respond within a reasonable period. In limited circumstances, we may lawfully refuse access or correction. Where appropriate, we will explain the reason." },
    ],
  },
  {
    n: 15,
    heading: "Anonymity and pseudonyms",
    blocks: [
      { type: "p", text: "Where practicable, you may interact with us anonymously or using a pseudonym." },
      { type: "p", text: "We may require your identity where it is necessary to:" },
      {
        type: "list",
        items: [
          "respond meaningfully to an enquiry;",
          "provide services;",
          "enter into a contract;",
          "issue an invoice;",
          "verify authority;",
          "comply with law; or",
          "protect confidential information.",
        ],
      },
    ],
  },
  {
    n: 16,
    heading: "Privacy complaints",
    blocks: [
      { type: "p", text: "To make a privacy complaint, contact:" },
      { type: "p", text: "Privacy Contact\nBehaviour Operating System\nEmail: tristan@behaviouroperatingsystem.com" },
      { type: "p", text: "Please include sufficient information for us to understand and investigate your complaint." },
      { type: "p", text: "We will acknowledge and investigate privacy complaints within a reasonable period." },
      { type: "p", text: "Where the Privacy Act applies and you are dissatisfied with our response, you may be entitled to contact the Office of the Australian Information Commissioner." },
    ],
  },
  {
    n: 17,
    heading: "Third-party websites",
    blocks: [
      { type: "p", text: "Our website may link to third-party websites or services." },
      { type: "p", text: "We are not responsible for the privacy, security or content practices of third parties. You should review their policies before providing information to them." },
    ],
  },
  {
    n: 18,
    heading: "Changes to this Privacy Policy",
    blocks: [
      { type: "p", text: "We may update this Privacy Policy to reflect changes to:" },
      {
        type: "list",
        items: [
          "our services;",
          "our technology;",
          "our service providers;",
          "our information-handling practices; or",
          "applicable legal requirements.",
        ],
      },
      { type: "p", text: "The current version will be published on this website with its effective date." },
    ],
  },
  {
    n: 19,
    heading: "Contact us",
    blocks: [
      { type: "p", text: "Questions about this Privacy Policy may be directed to:" },
      { type: "p", text: "Behaviour Operating System\nOperated by: Tristan Todd trading as Behaviour Operating System\nABN: 79 382 388 533\nEmail: tristan@behaviouroperatingsystem.com\nLocation: Sydney, New South Wales, Australia" },
    ],
  },
];
